import { accentA, dangerBg, dangerBorder, dangerText, overlay, stepTodoBg, stepTodoBorder, withAlpha, type ThemePalette } from '../theme/palette';

/**
 * The vertical, pinned-to-hand menu strip (design exploration 1d) with a
 * current-step readout at the top - port of the Unity `HandMenu`. This module
 * is the MODEL: which tiles exist, what each shows, and the tile palette. The
 * host binds it onto the static strip markup.
 */

/** Handlers for the hand menu tiles. Optional tiles are not built when their handler is absent. */
export interface HandMenuActions {
  readonly home: () => void;
  readonly tasks: () => void;
  readonly redo: () => void;
  readonly exit: () => void;
  /** Optional: switch the active training configuration. */
  readonly swap?: () => void;
  /**
   * Optional: "I need someone to help me" - show what the wearer has to tell
   * somebody in order to bring them into the session (the join code, D27).
   * On the hand menu rather than a controller button, because a wearer on
   * hand tracking has no controller to press.
   */
  readonly help?: () => void;
  /** Optional: mute/unmute the wearer's OWN microphone. A toggle: the tile shows the state, pressing flips it. */
  readonly micMute?: () => void;
  /** Optional: mute/unmute what the remote assistant sends back. Separate from micMute - different needs. */
  readonly assistantMute?: () => void;
  /** Optional: remove every assistant-drawn stroke, session-wide (the wearer-only clear-all). A command, not a toggle. */
  readonly clearDrawings?: () => void;
}

export type HandMenuTileId =
  | 'home'
  | 'tasks'
  | 'redo'
  | 'swap'
  | 'help'
  | 'mic'
  | 'hear'
  | 'clear'
  | 'exit';

/** The fixed tile order of the strip. Dividers sit after the readout and before EXIT. */
export const HAND_MENU_TILE_ORDER: readonly HandMenuTileId[] = [
  'home',
  'tasks',
  'redo',
  'swap',
  'help',
  'mic',
  'hear',
  'clear',
  'exit',
];

/** One tile's render state. */
export interface HandMenuTileState {
  readonly id: HandMenuTileId;
  readonly glyph: string;
  readonly label: string;
  /** Built at all? Optional tiles without a handler are absent from the strip. */
  readonly present: boolean;
  /** Can the wearer act on it right now? False renders disabled. */
  readonly enabled: boolean;
  /** Accent styling (HOME by default; an audio toggle whose audio is flowing). */
  readonly active: boolean;
  /** Danger styling (EXIT; an audio toggle whose audio is off). */
  readonly danger: boolean;
  readonly onPress: (() => void) | null;
}

/** Colours for one tile, in one place so a tile repainted at runtime cannot drift from one built at start. */
export interface TileColors {
  readonly fill: string;
  readonly fillHover: string;
  readonly border: string;
  readonly icon: string;
}

export function tileColors(t: ThemePalette, enabled: boolean, active: boolean, danger: boolean): TileColors {
  if (!enabled) {
    return { fill: stepTodoBg(t), fillHover: stepTodoBg(t), border: stepTodoBorder(t), icon: t.textLo };
  }
  if (active) {
    return { fill: accentA(t, 0.14), fillHover: accentA(t, 0.22), border: accentA(t, 0.5), icon: t.accent };
  }
  if (danger) {
    return { fill: dangerBg(t), fillHover: withAlpha(t.danger, 0.2), border: dangerBorder(t), icon: dangerText(t) };
  }
  return { fill: overlay(t, 0.05), fillHover: overlay(t, 0.1), border: overlay(t, 0.12), icon: t.secondaryText };
}

/** The initial tile states for a set of actions - the strip as `HandMenu.Build` lays it out. */
export function buildHandMenuTiles(actions: HandMenuActions): HandMenuTileState[] {
  const tile = (
    id: HandMenuTileId,
    glyph: string,
    label: string,
    onPress: (() => void) | undefined,
    extra: Partial<Pick<HandMenuTileState, 'active' | 'danger' | 'enabled' | 'present'>> = {},
  ): HandMenuTileState => ({
    id,
    glyph,
    label,
    present: extra.present ?? onPress !== undefined,
    // A null handler means "not available" - the tile renders disabled.
    enabled: extra.enabled ?? onPress !== undefined,
    active: extra.active ?? false,
    danger: extra.danger ?? false,
    onPress: onPress ?? null,
  });

  return [
    tile('home', '⌂', 'HOME', actions.home, { active: true }),
    tile('tasks', '☰', 'TASKS', actions.tasks),
    tile('redo', '⟳', 'REDO', actions.redo),
    tile('swap', '⇄', 'SWAP', actions.swap),
    tile('help', '☎', 'HELP', actions.help),
    // The two audio toggles are built in a neutral disabled state; the owner
    // pushes the real one as soon as it knows it, and on every change after.
    tile('mic', '○', 'MIC', actions.micMute, { enabled: false }),
    tile('hear', '◇', 'HEAR', actions.assistantMute, { enabled: false }),
    tile('clear', '⌫', 'CLEAR', actions.clearDrawings),
    tile('exit', '✕', 'EXIT', actions.exit, { danger: true }),
  ];
}

/**
 * Apply an audio-toggle state to a tile: `available` false renders disabled;
 * otherwise `on` picks accent (flowing) or danger (muted), because audio that
 * is off is the state worth noticing. Port of `HandMenu.Tile.Set`.
 */
export function withAudioState(
  tile: HandMenuTileState,
  glyph: string,
  label: string,
  available: boolean,
  on: boolean,
): HandMenuTileState {
  return {
    ...tile,
    glyph,
    label,
    enabled: available,
    active: available && on,
    danger: available && !on,
  };
}
